/* global document */
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import ReactCrop from 'react-image-crop';
import { addBarcodeValue } from 'apps/web_forms/slices/multimediaSlice';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useBarcodeScan from 'apps/web_forms/hooks/useBarcodeScan';

const onFinishRendering = setTimeout;

const StyledCropperContainer = styled.div`
  .ReactCrop__image {
    pointer-events: none;
  }

  /* Make cropper squares bigger */
  .ReactCrop__drag-handle::after {
    width: 20px;
    height: 20px;
  }

  .ord-nw {
    margin-top: -10px;
    margin-left: -10px;
  }
  .ord-n {
    margin-top: -10px;
    margin-left: -10px;
  }
  .ord-ne {
    margin-top: -10px;
    margin-right: -10px;
  }
  .ord-e {
    margin-top: -10px;
    margin-right: -10px;
  }
  .ord-se {
    margin-bottom: -10px;
    margin-right: -10px;
  }
  .ord-s {
    margin-bottom: -10px;
    margin-left: -10px;
  }
  .ord-sw {
    margin-bottom: -10px;
    margin-left: -10px;
  }
  .ord-w {
    margin-top: -10px;
    margin-left: -10px;
  }
`;

const INITIAL_CROP = {
  unit: '%',
  width: 50,
  height: 50,
  x: 25,
  y: 25,
};

const BarcodeCropper = ({ element, uploadedImageSrc }) => {
  const { barcode_type: barcodeType } = element;
  const restrictTo1D = barcodeType === '1d';
  const isDriverLicense = element.type === 'license';

  const thumbnailRef = useRef();
  const reactCropRef = useRef();
  const cropAreaRef = useRef();
  const uploadedImageRef = useRef();

  const [crop, setCrop] = useState(INITIAL_CROP);

  const [errorMessage, setErrorMessage] = useState('');
  const { barcodeFromImage } = useBarcodeScan();

  const dispatch = useDispatch();

  useEffect(
    function onElementChange() {
      resetCrop();
    },
    [element]
  );

  useEffect(() => {
    if (reactCropRef.current) {
      [cropAreaRef.current] = reactCropRef.current.getElementsByClassName(
        'ReactCrop__crop-selection'
      );

      const [rectCropImage] = reactCropRef.current.getElementsByClassName(
        'ReactCrop__image'
      );

      rectCropImage.style.maxHeight = '70vh';
    }
  }, [reactCropRef.current]);

  const changeCropperBackgroundColor = (color) => {
    if (cropAreaRef.current) {
      cropAreaRef.current.style.boxShadow = `0 0 0 9999em ${color}`;
    }
  };

  const resetCrop = () => {
    onFinishRendering(() => {
      changeCropperBackgroundColor('rgb(0, 0, 0, .5)');
      setCrop(INITIAL_CROP);
    });
  };

  /**
   * @param {HTMLImageElement} image - Image File Object
   * @param {Object} crop - crop Object
   * @param {String} fileName - Name of the returned file in Promise
   */
  function getCroppedImg(image, cropData) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = cropData.width;
    canvas.height = cropData.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      cropData.x * scaleX,
      cropData.y * scaleY,
      cropData.width * scaleX,
      cropData.height * scaleY,
      0,
      0,
      cropData.width,
      cropData.height
    );

    const base64Image = canvas.toDataURL('image/jpeg');

    return base64Image;
  }

  function onNewImageLoaded(image) {
    thumbnailRef.current = image;

    const img = document.createElement('img');
    img.src = uploadedImageSrc;

    uploadedImageRef.current = img;

    resetCrop();
  }

  function newFrameScan() {
    if (uploadedImageRef.current) {
      const originalWidth = uploadedImageRef.current.width;
      const thumbnailWidth = thumbnailRef.current.width;

      const resizeRatio = originalWidth / thumbnailWidth;

      const croppedImageSrc = getCroppedImg(uploadedImageRef.current, {
        ...crop,
        width: crop.width * resizeRatio,
        height: crop.height * resizeRatio,
        x: crop.x * resizeRatio,
        y: crop.y * resizeRatio,
      });

      barcodeFromImage(croppedImageSrc)
        .then((result) => {
          if (restrictTo1D && !result.is1DCode) {
            changeCropperBackgroundColor('#AA000050');
            setErrorMessage('Only 1D barcodes are allowed');
            return;
          }
          if (isDriverLicense && !result.isDriverLicenseCode) {
            changeCropperBackgroundColor('#AA000050');
            setErrorMessage('Only Driver License barcodes are allowed');
            return;
          }

          changeCropperBackgroundColor('#00ce7990');
          setErrorMessage('');

          dispatch(
            addBarcodeValue({
              key: element.identifier,
              value: result.text,
            })
          );
        })
        .catch(() => {
          changeCropperBackgroundColor('#00000050');
          setErrorMessage('');
        });
    }
  }

  return (
    <>
      <StyledCropperContainer ref={reactCropRef}>
        <ReactCrop
          src={uploadedImageSrc}
          crop={crop}
          keepSelection
          onChange={(newCrop) => {
            changeCropperBackgroundColor('rgb(0, 0, 0, .5)');
            setCrop(newCrop);
          }}
          onComplete={() => {
            newFrameScan();
          }}
          onImageLoaded={(image) => {
            onNewImageLoaded(image);
          }}
        />
      </StyledCropperContainer>
      {errorMessage !== undefined && (
        <ErrorMessage id={`${element.identifier}_errmsg`}>
          {errorMessage}
        </ErrorMessage>
      )}
    </>
  );
};

BarcodeCropper.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    hint: PropTypes.string,
    validate_msg: PropTypes.string,
    barcode_type: PropTypes.string,
    type: PropTypes.string,
    allow_manual_override: PropTypes.bool,
  }).isRequired,
  uploadedImageSrc: PropTypes.string.isRequired,
};

export default React.memo(BarcodeCropper);
